import {
  type StaffRequestQueueFilterValue,
  type StaffRequestQueueRow,
  staffRequestQueueFilterValues,
} from "@lafayette/schemas";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { EmptyState } from "../../ui/empty-state";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { useRequestViewer } from "../access";
import { hintClass, LookPill, linkClass, PriorityBadge, StateBadge } from "../components/parts";
import { describeRequestError } from "../errors";
import { formatAge, formatAgo, formatInstant } from "../format";
import {
  attentionLabels,
  attentionLook,
  COVERAGE_GAP_DETAIL,
  COVERAGE_GAP_LABEL,
  filterDescriptions,
  filterLabels,
  flagLabels,
  flagLook,
  typeLabel,
  workStatusLabels,
} from "../labels";
import { requestKeys, useRequestQueue } from "../queries";
import { useRequestsRuntime } from "../runtime";

// The request queue (MD-R1). Filters are the eleven closed filters of the contract plus "All open". The filter lives in the address
// (`?filter=`), so a refresh or the back button keeps it. Rows page by keyset: "Load more" sends the opaque `nextCursor` back. Aging
// is data (the age of each row); the page never judges a request as late. A request with no owner is an explicit exception, and a
// supervisor sees how many there are whatever filter is open.

const isFilter = (value: string | null): value is StaffRequestQueueFilterValue =>
  value !== null && (staffRequestQueueFilterValues as readonly string[]).includes(value);

const GAP_PAGE = 25;

export function RequestQueuePage() {
  useWideLayout();
  const { capabilities, staffUserId, displayName, isLoading: rolesLoading } = useRequestViewer();
  const [params, setParams] = useSearchParams();
  const raw = params.get("filter");
  const filter = isFilter(raw) ? raw : null;
  const queue = useRequestQueue(filter, capabilities.work);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.work) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include the request queue."
      />
    );
  }

  const choose = (next: StaffRequestQueueFilterValue | null) =>
    setParams(next === null ? {} : { filter: next }, { replace: false });
  const rows = queue.data?.pages.flatMap((page) => page.rows) ?? [];

  return (
    <section className="grid gap-5">
      <PageHeader
        title="Requests"
        description={
          <>
            The concierge requests you can work. Open one to ask the customer, record evidence,
            confirm and complete it.
            {displayName === null ? null : (
              <>
                {" "}
                <span id="request-viewer" className="block pt-1 text-sm">
                  Signed in as {displayName}. Requests that you hold are marked “Yours”.
                </span>
              </>
            )}
          </>
        }
      />

      {capabilities.reassignOwner ? (
        <CoverageGapBanner current={filter} onShow={() => choose("UNASSIGNED")} />
      ) : null}

      <div className="grid gap-2">
        <nav aria-label="Queue filters">
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            <FilterChip label="All open" active={filter === null} onClick={() => choose(null)} />
            {staffRequestQueueFilterValues.map((value) => (
              <FilterChip
                key={value}
                label={filterLabels[value]}
                active={filter === value}
                onClick={() => choose(value)}
              />
            ))}
          </ul>
        </nav>
        <p className={hintClass} id="request-filter-help">
          {filter === null ? "Every open request you can see." : filterDescriptions[filter]}
        </p>
      </div>

      {queue.isPending ? (
        <AsyncState kind="loading" title="Loading requests" message="Waiting for the answer." />
      ) : queue.isError && queue.data === undefined ? (
        <QueueError error={queue.error} onRetry={() => void queue.refetch()} />
      ) : (
        <div className="grid gap-3" aria-busy={queue.isFetching}>
          {queue.isRefetchError && !queue.isFetchNextPageError ? (
            <Notice tone="warning" role="status">
              <p>The last refresh failed, so this list may be out of date.</p>
              <Button variant="outline" size="sm" onClick={() => void queue.refetch()}>
                Refresh now
              </Button>
            </Notice>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p className={hintClass} role="status">
              {rows.length} {rows.length === 1 ? "request" : "requests"} shown. Updated{" "}
              {formatInstant(new Date(queue.dataUpdatedAt).toISOString())}.
            </p>
            <Button variant="outline" size="sm" onClick={() => void queue.refetch()}>
              <Icon name="refresh" />
              Refresh
            </Button>
          </div>
          <QueueTable
            rows={rows}
            supervisor={capabilities.reassignOwner}
            viewerStaffUserId={staffUserId}
            emptyMessage={
              filter === "AGING"
                ? "No request is flagged as aging. The server flags aging only when an aging threshold is set."
                : filter === "ASSIGNED_TO_ME"
                  ? "Nothing is assigned to you right now."
                  : "No request matches this filter right now."
            }
          />
          {queue.isFetchNextPageError ? (
            <Notice tone="warning" role="alert">
              <p>
                The next requests did not load. {describeRequestError(queue.error).message} The
                requests above are still shown.
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={queue.isFetchingNextPage}
                onClick={() => void queue.fetchNextPage()}
              >
                {queue.isFetchingNextPage ? "Loading more…" : "Retry loading more requests"}
              </Button>
            </Notice>
          ) : queue.hasNextPage ? (
            <div>
              <Button
                variant="outline"
                disabled={queue.isFetchingNextPage}
                onClick={() => void queue.fetchNextPage()}
              >
                {queue.isFetchingNextPage ? "Loading more…" : "Load more requests"}
              </Button>
            </div>
          ) : rows.length > 0 ? (
            <p className={hintClass}>End of the list.</p>
          ) : null}
        </div>
      )}
    </section>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: Readonly<{ label: string; active: boolean; onClick: () => void }>) {
  return (
    <li>
      <Button
        variant={active ? "default" : "outline"}
        size="sm"
        className="rounded-full"
        aria-pressed={active}
        aria-describedby="request-filter-help"
        onClick={onClick}
      >
        {active ? <Icon name="tick-02" /> : null}
        {label}
      </Button>
    </li>
  );
}

/** A supervisor sees every request that has no owner, whatever filter is open. */
function CoverageGapBanner({
  current,
  onShow,
}: Readonly<{ current: StaffRequestQueueFilterValue | null; onShow: () => void }>) {
  const { api } = useRequestsRuntime();
  const gaps = useQuery({
    queryKey: [...requestKeys.all, "coverage-gaps"] as const,
    queryFn: ({ signal }) => api.list({ filter: "UNASSIGNED", limit: GAP_PAGE }, { signal }),
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });
  const count = gaps.data?.rows.filter((row) => row.exception === "COVERAGE_GAP").length ?? 0;
  if (gaps.data === undefined || count === 0) return null;
  const more = gaps.data.nextCursor !== null;
  return (
    <Notice tone="warning" role="alert" className="rounded-lg px-4 py-3.5 sm:items-center">
      <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="min-w-0 flex-1 basis-80">
          <strong>
            {count}
            {more ? "+" : ""} {count === 1 && !more ? "request has" : "requests have"} no owner.
          </strong>{" "}
          {COVERAGE_GAP_DETAIL}
        </p>
        {current === "UNASSIGNED" ? null : (
          <Button variant="outline" size="sm" onClick={onShow}>
            Show the unassigned requests
          </Button>
        )}
      </div>
    </Notice>
  );
}

function QueueError({ error, onRetry }: Readonly<{ error: unknown; onRetry: () => void }>) {
  const failure = describeRequestError(error);
  if (failure.kind === "forbidden") {
    return <AsyncState kind="error" title="You cannot open this queue" message={failure.message} />;
  }
  return (
    <AsyncState
      kind="error"
      title="The queue did not load"
      message={failure.message}
      onRetry={onRetry}
    />
  );
}

function ownerText(row: StaffRequestQueueRow): string {
  if (row.owner !== null) return row.owner.displayName ?? "A staff member";
  if (row.assignee !== null) return `Assigned to ${row.assignee.displayName ?? "a staff member"}`;
  return "No owner";
}

/** True when the signed-in staff member holds the request: its owner, or its designated assignee while it is ASSIGNED. */
function heldBy(row: StaffRequestQueueRow, staffUserId: string | null): boolean {
  if (staffUserId === null) return false;
  return (row.owner ?? row.assignee)?.staffUserId === staffUserId;
}

const note = "block text-xs leading-5 font-normal text-muted-foreground";
const column = createColumnHelper<StaffRequestQueueRow>();

function QueueTable({
  rows,
  supervisor,
  viewerStaffUserId,
  emptyMessage,
}: Readonly<{
  rows: readonly StaffRequestQueueRow[];
  supervisor: boolean;
  viewerStaffUserId: string | null;
  emptyMessage: string;
}>) {
  const now = useRequestsRuntime().now();
  const columns = useMemo(
    () => [
      column.display({
        id: "request",
        header: "Request",
        meta: { rowHeader: true, className: "min-w-56 align-top" },
        cell: ({ row: { original: row } }) => (
          <div className="min-w-0">
            <Link
              id={`request-row-${row.id}`}
              className={`${linkClass} font-semibold`}
              to={`/requests/${encodeURIComponent(row.id)}`}
            >
              {row.title}
            </Link>
            <span className={note}>
              {typeLabel(row.requestType)} ·{" "}
              <span className="font-serif text-[15px] font-normal text-foreground">
                {row.customer.displayName}
              </span>
            </span>
            <span className={note}>{row.context.line ?? "No trip or item linked"}</span>
          </div>
        ),
      }),
      column.display({
        id: "state",
        header: "State",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <StateBadge state={row.state} />
            <PriorityBadge priority={row.priority} />
            {row.workStatus === "NONE" ? null : (
              <span className={note}>{workStatusLabels[row.workStatus]}</span>
            )}
          </span>
        ),
      }),
      column.display({
        id: "holder",
        header: "Holder",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            {ownerText(row)}
            {heldBy(row, viewerStaffUserId) ? (
              <LookPill look={{ tone: "brass", icon: "user" }}>Yours</LookPill>
            ) : null}
          </span>
        ),
      }),
      column.display({
        id: "age",
        header: "Age",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => (
          <div className="min-w-0">
            {formatAge(row.ageSeconds)}
            <span className={note}>
              Last customer activity {formatInstant(row.lastCustomerActivityAt)}
            </span>
            {row.awaitingCustomerSince === null ? null : (
              <span className={note}>
                Waiting for the customer since {formatInstant(row.awaitingCustomerSince)}
              </span>
            )}
          </div>
        ),
      }),
      column.display({
        id: "attention",
        header: "Attention",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            {row.exception === null ? null : (
              <LookPill look={flagLook.UNASSIGNED}>{COVERAGE_GAP_LABEL}</LookPill>
            )}
            {row.attention.map((kind) => (
              <LookPill key={kind} look={attentionLook[kind]}>
                {kind === "REQUEST_CUSTOMER_RESPONDED"
                  ? `Customer replied ${formatAgo(row.customerRespondedAt, now)}`
                  : attentionLabels[kind]}
              </LookPill>
            ))}
            {/* The flags that the attention chips already say (no owner, aging) are not drawn twice. */}
            {row.flags
              .filter((flag) => flag === "AWAITING_CUSTOMER")
              .map((flag) => (
                <LookPill key={flag} look={flagLook[flag]}>
                  {flagLabels[flag]}
                </LookPill>
              ))}
            {supervisor && row.exception !== null ? (
              // WALK-3: the name is "Assign" and the title of the row, so the Assign links of two rows differ. The title is read by id.
              <Link
                id={`request-assign-${row.id}`}
                className={linkClass}
                to={`/requests/${encodeURIComponent(row.id)}`}
                aria-labelledby={`request-assign-${row.id} request-row-${row.id}`}
              >
                Assign
              </Link>
            ) : null}
          </span>
        ),
      }),
    ],
    [now, supervisor, viewerStaffUserId],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(row) => row.id}
      caption="Requests in the queue"
      banded
      rowClassName={(row) =>
        row.exception === null ? undefined : "bg-warning-soft shadow-[inset_4px_0_0_var(--warning)]"
      }
      emptyState={<EmptyState title="No requests here" description={emptyMessage} />}
    />
  );
}
